import { Key, useMemo, useRef, useState } from "react";
import { pluralize } from "@/modules/pluralize";
import {
  MDSIconKind,
  MDSMenuDropdown,
  MDSMenuDropdownSlotName,
  MDSSelectMenu,
  MDSSelectMenuItem,
  MDSMenuInputHeader,
  MDSMenuItemSelectionVariant,
  MDSCollectionIcon,
  MDSToggleButton,
} from "@/mds";
import { isUndefined } from "lodash-es";
import { CollectionsFacetFilterToggleButton } from "./collections-facet-filter-toggle-button";
import { CollectionsFacetFilterNoResults } from "./collections-facet-filter-no-results";
import { CollectionsFacetFilterItem } from "./collections-facet-filter.types";

interface CollectionsFacetFilterProps {
  buttonLabel?: string;
  buttonPrefix?: string;
  searchText: string;
  onSearchTextChange: (value: string) => void;
  onSearchTextClear: () => void;
  items: CollectionsFacetFilterItem[];
  onSelectItem: (id: string) => void;
  onSelectClear: () => void;
  selectedModeKey: string;
  onSelectMode: (keys: Set<Key>) => void;
  noCollectionIsSelected: boolean;
  onNoCollectionIsSelected: (isSelected: boolean) => void;
  selectedItems: string[];
  supportNoCollectionOption?: boolean;
  supportModeSelection?: boolean;
}

export const CollectionsFacetFilter = ({
  buttonLabel,
  buttonPrefix,
  items,
  searchText,
  onSearchTextChange,
  onSearchTextClear,
  onSelectItem,
  onSelectClear,
  selectedModeKey,
  onSelectMode,
  noCollectionIsSelected,
  onNoCollectionIsSelected,
  selectedItems,
  supportNoCollectionOption,
  supportModeSelection = true,
}: CollectionsFacetFilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasSelectedItems = selectedItems.length > 0;
  const dropdownListRef = useRef<HTMLDivElement>(null);

  const showNoCollectionOption = supportNoCollectionOption && selectedModeKey === "in-any-of";

  const computedButtonLabel = useMemo(() => {
    const count = selectedItems.length;
    if (!isUndefined(buttonPrefix)) {
      if (selectedItems.length === 1) {
        return `${buttonPrefix} ${selectedItems[0]}`;
      }

      const conjunction = selectedModeKey === "in-any-of" ? "or" : "and";

      if (count > 1) {
        return `${buttonPrefix} ${selectedItems[0]} ${conjunction} ${count - 1} ${pluralize(count - 1, "other", "others")}`;
      }

      return `${buttonLabel}`;
    }

    if (!count) {
      return `${buttonLabel}`;
    }

    return `${selectedItems.length} ${buttonLabel}`;
  }, [buttonLabel, buttonPrefix, selectedItems, selectedModeKey]);

  return (
    <MDSMenuDropdown data-test-id="collections-facet-filter" onOpenChange={setIsOpen}>
      <MDSToggleButton
        slot={MDSMenuDropdownSlotName.Trigger}
        isOpen={isOpen}
        iconKind={MDSIconKind.Collection}
        isToggled={hasSelectedItems}
        label={computedButtonLabel}
        onClear={() => onSelectClear()}
      />
      <div slot={MDSMenuDropdownSlotName.Menu}>
        <MDSMenuInputHeader
          onChange={onSearchTextChange}
          onClear={onSearchTextClear}
          value={searchText}
          onKeyDirection={() => {
            if (dropdownListRef.current) dropdownListRef.current.focus();
          }}
          onEscape={() => {
            if (searchText) onSearchTextChange("");
            else dropdownListRef.current?.focus();
          }}
          placeholder="Add to existing or new collection"
        />
        {supportModeSelection && (
          <CollectionsFacetFilterToggleButton
            onSelectMode={onSelectMode}
            selectedModeKey={selectedModeKey}
          />
        )}
        <MDSSelectMenu
          ref={dropdownListRef}
          aria-label="Filter by collections"
          selectionBehavior="toggle"
          selectionMode="single"
          shouldFocusWrap={true}
          style={{ maxHeight: "50vh", maxWidth: 320 }}
        >
          {showNoCollectionOption && (
            <MDSSelectMenuItem
              id={"no-collection"}
              label={"No collection"}
              icon={() => <MDSCollectionIcon size={11} />}
              onClick={() => onNoCollectionIsSelected(!noCollectionIsSelected)}
              isSelected={noCollectionIsSelected}
              selectionVariant={MDSMenuItemSelectionVariant.RadioButton}
            />
          )}
          {items.map(item => (
            <MDSSelectMenuItem
              key={item.id}
              id={item.id}
              label={item.title}
              subtitle={item.subtitle}
              icon={() => <MDSCollectionIcon collectionId={item.id} size={11} />}
              onClick={() => onSelectItem(item.id)}
              isSelected={item.isSelected}
              selectionVariant={MDSMenuItemSelectionVariant.RadioButton}
            />
          ))}
          {items.length === 0 && <CollectionsFacetFilterNoResults searchText={searchText} />}
        </MDSSelectMenu>
      </div>
    </MDSMenuDropdown>
  );
};
